import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getCart, GetCartApiParameter, updateCart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoNewIcon from "@MasterBluePrint/Icons/InfoNew";
import { getQuote, GetQuoteApiParameter } from "@MasterBluePrint/Services/CustomQuoteService";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, Fragment, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        currentPageUrl,
        billToState: getBillToState(state, cart?.billToId),
        language: state.context.session.language,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    checkoutSidebarContainer?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingLabelNew?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    sideWrapper?: InjectableCss;
    tooltip?: InjectableCss;
    tooltipHeading?: InjectableCss;
    tooltipContent?: InjectableCss;
    tooltipIcon?: InjectableCss;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            background-color: #fff;
            padding: 1.25rem;
            > div {
                width: calc(100% + 10px) !important;
            }
        `,
    },
    checkoutSidebarContainer: {
        gap: 10,
        css: css`
            background-color: transparent;
            padding: 0;
            > div {
                width: calc(100% + 10px) !important;
            }
        `,
    },
    labelGridItem: { width: 6 },
    valueGridItem: { width: 6 },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    promotionValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsValue: {
        css: css`
            margin-left: auto;

            .discount-text {
                font-size: 1rem;
                font-weight: 500;
                letter-spacing: -0.31px;
                line-height: 1.4375rem;
                width: 100%;
                text-align: right;
                justify-content: right;
                display: flex;
            }
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxValue: {
        css: css`
            margin-left: auto;

            &.size-adjust {
                color: #333F48;
                font-family: 'Roboto',sans-serif;
                font-size: 1rem;
                letter-spacing: -.019rem;
                line-height: 1.438rem;
                font-weight: 400;
            }
        `,
    },
    totalLabel: { weight: "bold" },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
    sideWrapper: {
        css: css`
            span.cart-items {
                border-bottom: 1px solid #C2D1D9;
                display: none;
                color: #333F48;
                font-family: 'Lato',sans-serif;
                font-size: .9375rem;
                font-weight: bold;
                letter-spacing: 0.17px;
                line-height: 1.125rem;
                padding-bottom: .8125rem;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-bottom: .9375rem;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            }
        `,
    },
    shippingAndHandlingLabel: {
        css: css`
            position: relative;
            font-size: 1rem;
            font-weight: 500;
            -webkit-letter-spacing: -.019rem;
            -moz-letter-spacing: -.019rem;
            -ms-letter-spacing: -.019rem;
            letter-spacing: -.019rem;
            line-height: 1.438rem;
        `,
    },
    shippingAndHandlingLabelNew: {
        css: css`
            position: relative;
            font-size: 1rem;
            font-weight: 500;
            -webkit-letter-spacing: -0.31px;
            -moz-letter-spacing: -0.31px;
            -ms-letter-spacing: -0.31px;
            letter-spacing: -0.31px;
            line-height: 1.4375rem;
            width: 100%;
            text-align: right;
            justify-content: right;
            display: flex;
        `,
    },
    taxLabel: {
        css: css`
            position: relative;
            font-size: 1rem !important;
            font-weight: 500;
            -webkit-letter-spacing: -0.31px;
            -moz-letter-spacing: -0.31px;
            -ms-letter-spacing: -0.31px;
            letter-spacing: -0.31px !important;
            line-height: 1.4375rem;
            width: 100%;
        `,
    },
    tooltipIcon: {
        css: css`
            display: inline-block;
            position: absolute;
            margin-left: 5px;
            z-index: 9;
            cursor:pointer;
            
            svg {
                width: 1.5rem;
                height: 1.5rem;
                scale: 0.8;
            }
        `,
    },
    tooltip: {
        css: css`
            background-color: #fff;
            box-shadow: 0 7px 17px 0 rgba(0,37,84,0.23);
            position: absolute;
            width: 250px;
            padding: 1.5625rem;
            right: -72px;
            bottom: 2.375rem;
            display: none;
            z-index: 2;

            &.active {
                display: block;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            right:-106px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    tooltipHeading: {
        css: css`
            position: relative;
            color: #002554;
            font-family: Roboto;
            font-size: 1.125rem;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 31px;
            margin-bottom: 20px;
            text-align: left;
            &:after {
                height: 3px;
                width: 0.665rem;;
                background-color: #C63527;
                content: '';
                display: inline-block;
                position: absolute;
                bottom: -2px;
                left: 1px;
            }
        `,
    },
    tooltipContent: {
        css: css`
            color: #424B5A;
            font-family: Lato;
            font-size: .9375rem;
            letter-spacing: 0.17px;
            line-height: 1.5rem;
            text-align: left !important;

            &:after {
                content: '';
                display: block;
                height: 0;
                position: absolute;
                width: 0;
                border: 12px solid;
                border-color: #fff transparent transparent transparent;
                z-index: 99;
                bottom: -1.4375rem;
                right: 45px;
            }

            @media screen and (min-width: 1023px) and (max-width: 1100px) {
                &:after {
                    right: 80px !important;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &:after {
                                right: 80px !important;
                            }
                        `,
                        css`
                            &:after {
                                right: 100px;
                            }
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    extendedStyles,
    currentPageUrl,
    billToState,
    language
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));
    const [showText, setShowText] = React.useState(false);
    const shippingHandlingText = siteMessage("Shipping_and_Handling_Tooltip").toString();
    const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";
    const [quote, setQuote] = React.useState<QuoteModel | null>(null);
    const [isQuoteCart, setIsQuoteCart] = React.useState(false);
    const [quoteGrandTotal, setQuoteGrandTotal] = React.useState(0);
    const [quoteGrandTotalDisplay, setQuoteGrandTotalDisplay] = React.useState("");
    useEffect(() => {
        if (cart) {
            if (cart.properties?.isquote === "true") {
                setIsQuoteCart(true);
                const parameter: GetQuoteApiParameter = { quoteId: cart.properties?.quoteNumber };
                getQuoteData(parameter).then(quote => {
                    setQuote(quote);
                });
            }
        }
    }, [cart]);

    React.useEffect(() => {
        if (cart && cart.properties?.isquote === "true") {
            let orderTotal: any;
            if (isQuoteCart && quote) {
                orderTotal = cart.shippingAndHandling > 0 ? quote.orderSubTotal + cart.shippingAndHandling + quote.totalTax : quote.orderSubTotal + quote.totalTax;
                setQuoteGrandTotal(orderTotal);
            }
            if (language != null) {
                const currencySymbol = cart.currencySymbol;
                const localizedOrderTotal = getLocalizedCurrency({ amount: orderTotal, language, currencySymbol });
                setQuoteGrandTotalDisplay(localizedOrderTotal);
            }
        }
    }, [cart, quote]);

    if (isLoading || !cart) {
        return (
            <GridContainer {...styles.container} className="side-wrapper-top">
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel} className="total-label">{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem} className="total-value">
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>
                                {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    if (isCartEmpty) {
        return (
            <GridContainer {...styles.container} className="side-wrapper-top">
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel} className="total-label">{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.totalValue} className="total-value">{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>
                                {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    if (currentPageUrl.toLowerCase().includes("cart")) {
        if (!language) {
            return null;
        }
        const orderTotal = cart.shippingAndHandling > 0 ? cart.orderGrandTotal - cart.shippingAndHandling : cart.shippingAndHandling;
        const currencySymbol = cart.currencySymbol;
        const localizedOrderTotal = getLocalizedCurrency({ amount: orderTotal, language, currencySymbol });

        return (
            <Typography as="div" {...styles.sideWrapper} className="side-wrapper">
                {cart?.cartLines?.length === 1 ?
                    <Typography className="cart-items"> {`${translate("There is")} ${cart?.cartLines?.length} ${translate("item in your cart")}`}</Typography> :
                    <Typography className="cart-items"> {`${translate("There are")} ${cart?.cartLines?.length} ${translate("items in your cart")}`}</Typography>
                }
                <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`} className="grid-container">
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                            {isQuoteCart ? quote?.orderSubTotalDisplay : cart.orderSubTotalDisplay}
                        </Typography>
                    </GridItem>
                    {orderPromotions &&
                        orderPromotions.map(promotion => (
                            <Fragment key={promotion.id}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.promotionLabel} data-test-selector="cartTotalOrderPromotionName">
                                        {`${translate("Promotion")}: ${promotion.name}`}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.promotionValue} data-test-selector="cartTotalOrderPromotionValue">
                                        {`-${promotion.amountDisplay}`}
                                    </Typography>
                                </GridItem>
                            </Fragment>
                        ))}

                    {showTaxAndShipping && (cart.shippingAndHandlingDisplay.length > 0 || (quote && quote.shippingAndHandlingDisplay.length > 0)) && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}
                                    <Typography as="span" {...styles.tooltipIcon}
                                        onClick={() => setShowText(!showText)}
                                        onKeyDown={(e: any) => e.keyCode === 27 ? setShowText(false) : ''}
                                        onBlur={() => setShowText(false)}
                                    >
                                        <Icon src={InfoNewIcon} />
                                    </Typography>
                                    <Typography as='div' {...styles.tooltip} className={`${showText ? 'active' : ''}`} >
                                        <Typography as='h5' {...styles.tooltipHeading}>{translate("Shipping & Handling")} </Typography>
                                        <Typography as='div' {...styles.tooltipContent}>{shippingHandlingText} </Typography>
                                    </Typography>
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.shippingAndHandlingValue}
                                    data-test-selector="cartTotalShippingAndHandlingAmount"
                                >
                                    {isQuoteCart ?
                                        (quote?.shippingAndHandling === 0 || !billToState.value || !isNonEmptyString(billToState?.value?.email) || !isNonEmptyString(billToState?.value?.address1) ? (
                                            <Typography {...styles.shippingAndHandlingLabelNew}>
                                                {translate("ShippingandHandlingYetToBeCalculated")}
                                            </Typography>
                                        ) :
                                            quote?.shippingAndHandlingDisplay)
                                        : (cart.shippingAndHandling === 0 || !billToState.value || !isNonEmptyString(billToState?.value?.email) || !isNonEmptyString(billToState?.value?.address1) ? (
                                            <Typography {...styles.shippingAndHandlingLabelNew}>
                                                {translate("ShippingandHandlingYetToBeCalculated")}
                                            </Typography>
                                        ) :
                                            cart.shippingAndHandlingDisplay)
                                    }
                                </Typography>
                            </GridItem>
                        </>
                    )}
                    {shippingPromotions &&
                        shippingPromotions.map(promotion => (
                            <Fragment key={promotion.id}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.promotionLabel}>
                                        {`${translate("Promotion")}: ${promotion.name}`}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.promotionValue} data-test-selector="cartTotalShippingPromotionValue">
                                        {`-${promotion.amountDisplay}`}
                                    </Typography>
                                </GridItem>
                            </Fragment>
                        ))}
                    {isQuoteCart ?
                        (showTaxAndShipping && quote && quote.totalTaxDisplay.length > 0 && quote.customerOrderTaxes!.length === 0 && (
                            <>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    {quote.isAuthenticated === true && !quote.totalTaxDisplay.toLocaleLowerCase().includes("calculated") ?
                                        (<Typography {...styles.taxValue} className="cart-tax-value" data-test-selector="cartTotal_totalTaxDisplay">
                                            {quote.totalTaxDisplay}
                                        </Typography>) :
                                        <Typography {...styles.taxLabel} className="cart-tax-label" data-test-selector="cartTotal_totalTaxDisplay">
                                            {quote.totalTaxDisplay}
                                        </Typography>}
                                </GridItem>
                            </>
                        ))
                        :
                        (showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                            <>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    {cart.isAuthenticated === true && !cart.totalTaxDisplay.toLocaleLowerCase().includes("calculated") ?
                                        (<Typography {...styles.taxValue} className="cart-tax-value" data-test-selector="cartTotal_totalTaxDisplay">
                                            {cart.totalTaxDisplay}
                                        </Typography>) :
                                        <Typography {...styles.taxLabel} className="cart-tax-label" data-test-selector="cartTotal_totalTaxDisplay">
                                            {cart.totalTaxDisplay}
                                        </Typography>}
                                </GridItem>
                            </>
                        ))}
                    {showTaxAndShipping && !isQuoteCart &&
                        cart.customerOrderTaxes!.map(tax => {
                            const key = `${tax.taxCode}_${tax.sortOrder}`;
                            return (
                                <Fragment key={key}>
                                    <GridItem {...styles.labelGridItem}>
                                        <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.valueGridItem}>
                                        <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                    </GridItem>
                                </Fragment>
                            );
                        })}
                    {showTaxAndShipping && isQuoteCart && quote &&
                        quote.customerOrderTaxes!.map(tax => {
                            const key = `${tax.taxCode}_${tax.sortOrder}`;
                            return (
                                <Fragment key={key}>
                                    <GridItem {...styles.labelGridItem}>
                                        <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.valueGridItem}>
                                        <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                    </GridItem>
                                </Fragment>
                            );
                        })}
                    {discountTotal !== undefined && discountTotal > 0 && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.discountsValue}>
                                    <Typography className="discount-text">{`${translate("You saved")} `}{" "}</Typography>
                                    <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                                </Typography>
                            </GridItem>
                        </>
                    )}
                    {showTaxAndShipping && (
                        <>
                            <Typography as="span" className="border-seperator"></Typography>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.totalLabel} className="total-label">{translate("Total")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.totalValue} className="total-value" data-test-selector="cartTotalOrderGrandTotalDisplay">
                                    <>
                                        {isQuoteCart ?
                                            (quote?.shippingAndHandling === 0 || !billToState.value || !isNonEmptyString(billToState?.value?.email) || !isNonEmptyString(billToState?.value?.address1) ? (
                                                <>
                                                    {quote && quote?.shippingAndHandling > 0 ? (localizedOrderTotal) : quote?.orderGrandTotalDisplay}
                                                </>
                                            ) : quote?.orderGrandTotalDisplay
                                            ) :
                                            (cart.shippingAndHandling === 0 || !billToState.value || !isNonEmptyString(billToState?.value?.email) || !isNonEmptyString(billToState?.value?.address1) ? (
                                                <>
                                                    {cart.shippingAndHandling > 0 ? (localizedOrderTotal) : cart.orderGrandTotalDisplay}
                                                </>
                                            ) : cart.orderGrandTotalDisplay
                                            )}
                                    </>
                                </Typography>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </Typography>
        );
    }
    if (currentPageUrl.toLowerCase().includes("checkout")) {
        return (
            <Typography as="div" {...styles.sideWrapper} className="side-wrapper">
                <GridContainer {...styles.checkoutSidebarContainer} data-test-selector={`cartTotal_${cart.id}`} className="grid-container">
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                            {isQuoteCart ? quote?.orderSubTotalDisplay : cart.orderSubTotalDisplay}
                        </Typography>
                    </GridItem>
                    {orderPromotions &&
                        orderPromotions.map(promotion => (
                            <Fragment key={promotion.id}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.promotionLabel} data-test-selector="cartTotalOrderPromotionName">
                                        {`${translate("Promotion")}: ${promotion.name}`}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.promotionValue} data-test-selector="cartTotalOrderPromotionValue">
                                        {`-${promotion.amountDisplay}`}
                                    </Typography>
                                </GridItem>
                            </Fragment>
                        ))}

                    {showTaxAndShipping && (cart.shippingAndHandlingDisplay.length > 0 || (quote && quote.shippingAndHandlingDisplay.length > 0)) && (
                        <>
                            <GridItem {...styles.labelGridItem} className="checkout-shipping-msg">
                                <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}
                                    <Typography as="span" {...styles.tooltipIcon}
                                        onClick={() => setShowText(!showText)}
                                        onKeyDown={(e: any) => e.keyCode === 27 ? setShowText(false) : ''}
                                        onBlur={() => setShowText(false)}
                                    >
                                        <Icon src={InfoNewIcon} />
                                    </Typography>
                                    <Typography as='div' {...styles.tooltip} className={`${showText ? 'active' : ''}`} >
                                        <Typography as='h5' {...styles.tooltipHeading}>{translate("Shipping & Handling")} </Typography>
                                        <Typography as='div' {...styles.tooltipContent} className="checkout-tooltip-content">{shippingHandlingText} </Typography>
                                    </Typography>
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.shippingAndHandlingValue}
                                    data-test-selector="cartTotalShippingAndHandlingAmount"
                                >

                                    {cart.shipVia && cart.carrier ? (
                                        cart?.carrier?.description && cart?.carrier?.description?.toLowerCase().endsWith("collect") && (cart.shippingAndHandlingDisplay === '£0.00' || cart.shippingAndHandlingDisplay === '$0.00') ? (
                                            <Typography {...styles.shippingAndHandlingLabel}>
                                                {translate("charged to your account")}
                                            </Typography>
                                        ) :
                                            cart.shippingAndHandlingDisplay

                                    ) :
                                        cart.shippingAndHandlingDisplay === '£0.00' || cart.shippingAndHandlingDisplay === '$0.00' ? (
                                            <Typography {...styles.shippingAndHandlingLabelNew}>
                                                {translate("ShippingandHandlingYetToBeCalculated")}
                                            </Typography>
                                        ) :
                                            cart.shippingAndHandlingDisplay
                                    }
                                </Typography>
                            </GridItem>
                        </>
                    )}
                    {shippingPromotions &&
                        shippingPromotions.map(promotion => (
                            <Fragment key={promotion.id}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.promotionLabel}>
                                        {`${translate("Promotion")}: ${promotion.name}`}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.promotionValue} data-test-selector="cartTotalShippingPromotionValue">
                                        {`-${promotion.amountDisplay}`}
                                    </Typography>
                                </GridItem>
                            </Fragment>
                        ))}
                    {isQuoteCart ? (
                        showTaxAndShipping && quote && quote.totalTaxDisplay.length > 0 && quote.customerOrderTaxes!.length === 0 && (
                            <>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    {quote.isAuthenticated === true ?
                                        (<Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay" className={`${quote.totalTaxDisplay === "Calculated at Checkout" ? 'size-adjust' : ''}`}>
                                            {quote.totalTaxDisplay}
                                        </Typography>) :
                                        <Typography {...styles.taxLabel} data-test-selector="cartTotal_totalTaxDisplay">
                                            {quote.totalTaxDisplay}
                                        </Typography>}
                                </GridItem>
                            </>
                        )) : (showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                            <>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    {cart.isAuthenticated === true ?
                                        (<Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay" className={`${cart.totalTaxDisplay === "Calculated at Checkout" ? 'size-adjust' : ''}`}>
                                            {cart.totalTaxDisplay}
                                        </Typography>) :
                                        <Typography {...styles.taxLabel} data-test-selector="cartTotal_totalTaxDisplay">
                                            {cart.totalTaxDisplay}
                                        </Typography>}
                                </GridItem>
                            </>
                        ))}
                    {showTaxAndShipping && !isQuoteCart &&
                        cart.customerOrderTaxes!.map(tax => {
                            const key = `${tax.taxCode}_${tax.sortOrder}`;
                            return (
                                <Fragment key={key}>
                                    <GridItem {...styles.labelGridItem}>
                                        <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.valueGridItem}>
                                        <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                    </GridItem>
                                </Fragment>
                            );
                        })}
                    {discountTotal !== undefined && discountTotal > 0 && !isQuoteCart && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.discountsValue}>
                                    <Typography className="discount-text">{`${translate("You saved")} `}{" "}</Typography>
                                    <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                                </Typography>
                            </GridItem>
                        </>
                    )}
                    {showTaxAndShipping && !isQuoteCart ? (
                        <>
                            <Typography as="span" className="border-seperator"></Typography>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.totalLabel} className="total-label">{translate("Total")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.totalValue} className="total-value" data-test-selector="cartTotalOrderGrandTotalDisplay">
                                    {cart.orderGrandTotalDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    ) : (<>
                        <Typography as="span" className="border-seperator"></Typography>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel} className="total-label">{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.totalValue} className="total-value" data-test-selector="cartTotalOrderGrandTotalDisplay">
                                {quoteGrandTotalDisplay}
                            </Typography>
                        </GridItem>
                    </>)}
                </GridContainer>
            </Typography>
        );
    }
    return (
        <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`} className="cart-total">
            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabel} className="order-subtotal">
                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                </Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal" className="order-subtotal-value">
                    {isQuoteCart ? quote?.orderSubTotalDisplay : cart.orderSubTotalDisplay}
                </Typography>
            </GridItem>
            {orderPromotions &&
                orderPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel} data-test-selector="cartTotalOrderPromotionName" className="order-label">
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.promotionValue} data-test-selector="cartTotalOrderPromotionValue" className="order-value">
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.shippingAndHandlingLabel} className="order-label">{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography
                            {...styles.shippingAndHandlingValue}
                            data-test-selector="cartTotalShippingAndHandlingAmount"
                            className="order-value"
                        >
                            {cart.shipVia && cart.carrier ? (
                                cart?.carrier?.description && cart?.carrier?.description?.toLowerCase().endsWith("collect") && (cart.shippingAndHandlingDisplay === '£0.00' || cart.shippingAndHandlingDisplay === '$0.00') ? (
                                    <Typography {...styles.shippingAndHandlingLabel}>
                                        {translate("charged to your account")}
                                    </Typography>
                                ) :
                                    cart.shippingAndHandlingDisplay

                            ) :
                                cart.shippingAndHandlingDisplay === '£0.00' || cart.shippingAndHandlingDisplay === '$0.00' ? (
                                    <>
                                        {translate("ShippingandHandlingYetToBeCalculated")}
                                    </>
                                ) :
                                    cart.shippingAndHandlingDisplay
                            }

                        </Typography>
                    </GridItem>
                </>
            )}
            {shippingPromotions &&
                shippingPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel} className="order-label">
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.promotionValue} data-test-selector="cartTotalShippingPromotionValue" className="order-value">
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {isQuoteCart ? (
                showTaxAndShipping && quote && quote.totalTaxDisplay.length > 0 && quote.customerOrderTaxes!.length === 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.taxLabel} className="order-label">{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay" className="order-value">
                                {quote.totalTaxDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )) : (
                showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.taxLabel} className="order-label">{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay" className="order-value">
                                {cart.totalTaxDisplay}
                            </Typography>
                        </GridItem>
                    </>
                ))}
            {showTaxAndShipping &&
                cart.customerOrderTaxes!.map(tax => {
                    const key = `${tax.taxCode}_${tax.sortOrder}`;
                    return (
                        <Fragment key={key}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography className="order-label">{tax.taxDescription || translate("Tax")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.taxValue} className="order-value">{tax.taxAmountDisplay}</Typography>
                            </GridItem>
                        </Fragment>
                    );
                })}
            {showTaxAndShipping && (
                <>
                    <GridItem {...styles.labelGridItem} className="order-total" >
                        <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem} className="order-total-value">
                        <Typography {...styles.totalValue} data-test-selector="cartTotalOrderGrandTotalDisplay">
                            {isQuoteCart && quote ? quoteGrandTotalDisplay : cart.orderGrandTotalDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {discountTotal !== undefined && discountTotal > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.discountsValue}>
                            <Typography className="discount-text">{`${translate("You saved")} `}{" "}</Typography>
                            <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};
export function getQuoteData(
    parameter: GetQuoteApiParameter,
): Promise<QuoteModel> {
    const quoteProduct = getQuote(parameter);
    return quoteProduct;
}
export default connect(mapStateToProps)(CartTotalDisplay);
